import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from './activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import {
  ListItemType,
  RecyclerList,
  Props as RecyclerListProps,
} from './recycler-list';
import { ListOption } from './list-item';
import { SCREEN_PADDING } from '@/styles';
import { TextInput } from './text-input';

export type Props<TListOption> = RecyclerListProps<TListOption> & {
  creatable?: boolean;
  creatableText?: string;
  initialSearchText?: string;
  isLoading?: boolean;
  label: string;
  listHeader?: React.ReactNode;
  onSearchInputChange?: (input: string) => void;
  placeholder: string;
};

export const Selector = <TListOption extends ListOption = ListOption>({
  initialSearchText = '',
  creatable,
  creatableText,
  isLoading,
  label,
  listHeader,
  onSearchInputChange,
  onSelect,
  placeholder,
  sections,
  ...listProps
}: Props<TListOption>) => {
  const [styles] = useStyles();

  const [searchText, setSearchText] = useState(initialSearchText);
  const listSections = useMemo(() => {
    let result = sections;
    if (searchText && !isLoading) {
      result = [
        {
          data: result.reduce<TListOption[]>((acc, section) => {
            return acc.concat(section.data);
          }, []),
          key: null,
        },
      ];

      if (creatable) {
        result.push({
          data: [
            {
              title: creatableText || searchText,
              value: ListItemType.Creatable,
            } as TListOption,
          ],
          key: null,
        });
      }
    }

    return result;
  }, [creatable, creatableText, isLoading, searchText, sections]);

  return (
    <>
      <TextInput
        autoFocus
        containerStyle={styles.textInput}
        label={searchText ? label : ''}
        onChangeText={(input) => {
          setSearchText(input);
          onSearchInputChange?.(input);
        }}
        placeholder={placeholder}
        returnKeyType="search"
        rightElement={isLoading ? <ActivityIndicator /> : null}
        value={searchText}
      />
      {listHeader}
      <RecyclerList
        onSelect={(option) => {
          if (creatableText && option.value === ListItemType.Creatable) {
            return onSelect?.({
              ...option,
              title: searchText,
            });
          }
          onSelect?.(option);
        }}
        sections={listSections}
        {...listProps}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  textInput: {
    backgroundColor: theme.colors.background,
    marginBottom: 12,
    marginHorizontal: SCREEN_PADDING,
  },
}));
